import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useEffect, useReducer } from 'react';
import { useAnalyzerSerialAutocomplete } from '../hooks/useAnalyzerSerialAutocomplete';
import { DialogSize, Form } from '../utils/sharedTypes';

const EditItemDialog = ({
  editModalOpen,
  setEditModalOpen,
  itemUpdater,
  itemGetter,
  itemId,
  children,
  handlerMiddleware,
  itemValidator,
  formInstructions,
  itemName,
  dialogSize,
}: {
  editModalOpen: boolean;
  setEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  itemUpdater: Function;
  itemGetter: Function;
  // Item id used by itemGetter (called with {id: Number(itemId)}) to retrieved the item to edit
  itemId: number | string;
  // Function that returns the add/edit form elements; each form element should be controlled, with
  //   a name prop containing a string that matches the field on the form object that that element
  //   changes; the value of each form element should be set to formState.fieldName, where fieldName
  //   matches the string of the name prop; handler should be keyed to relevant onChange props; enterHandler
  //   should be keyed to any relevant onKeyDown props
  children: (formState: any, handler: any, enterHandler?: any) => JSX.Element;
  // Function called within the form change handlers; the resulting object will be combined with
  //  the name / value pair from the change event, with the results of the handlerMiddleware function
  //  overriding the name / value pair, if applicable. The user of this component is expected to handle any
  //  results from the Autocomplete Material UI component; this component will only handle simple name/value
  //  pairs set as described above.
  handlerMiddleware: (
    name: string,
    value: string,
    state: Form,
    autoboxValue?: any,
    autoboxLabel?: string
  ) => object;
  // Function that evaluates one of selectedItems, returning true if the item may be saved/edited,
  //  and returning false otherwise
  itemValidator: (item: any) => boolean;
  // Instructions displayed when itemValidator has returned false
  formInstructions: string;
  // Optional item name that replaces "Item" in dialog headers
  itemName?: string;
  // Optional dialog size; defaults to "md"
  dialogSize?: DialogSize;
}) => {
  const { data: itemToEdit } = itemGetter({ id: Number(itemId) });
  const formState = itemToEdit;
  const analyzerAutoComplete = useAnalyzerSerialAutocomplete();
  const [editFormState, setEditFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    formState
  );
  useEffect(() => {
    setEditFormState(formState);
  }, [itemToEdit, formState, editModalOpen]);
  if (
    formState === undefined ||
    editFormState === undefined ||
    Object.keys(editFormState).length < 1
  )
    return null;

  const handleFormChange = (
    event: any,
    autoboxValue?: object,
    autoboxLabel?: string
  ) => {
    const { name, value } = event.target;
    const middlewareResult = handlerMiddleware(
      name,
      value,
      editFormState,
      autoboxValue,
      autoboxLabel
    );
    const autoCompleteResults = analyzerAutoComplete(name, value);
    setEditFormState({
      [name]: value,
      ...autoCompleteResults,
      ...middlewareResult,
    });
  };
  const handleEditModalClose = () => {
    setEditModalOpen(false);
  };
  const handleEditModalSubmit = async () => {
    await itemUpdater(editFormState, itemId);
    setEditModalOpen(false);
  };
  return (
    <Dialog
      maxWidth={dialogSize ? dialogSize : 'md'}
      fullWidth
      open={editModalOpen}
      aria-labelledby='form-dialog-title'
    >
      <DialogTitle id='form-dialog-title'>
        Edit {itemName ? itemName : 'Item'}
      </DialogTitle>
      <DialogContent>
        {children(editFormState, handleFormChange)}
        {!itemValidator(editFormState) && (
          <Typography style={{ marginTop: '1rem' }} color='error'>
            {formInstructions}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEditModalClose} color='error'>
          Cancel
        </Button>
        {itemValidator(editFormState) && (
          <Button onClick={handleEditModalSubmit} color='primary'>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditItemDialog;
